import { useEffect, useMemo, useState } from "react";
import { tryApiGet } from "../api/client";
import { useAppData } from "../state/AppData";
import { navigate } from "../lib/router";
import { LIVE_WINDOW_ORDER, type LiveWindow } from '../lib/definitions/analysis';
import { colorFor, STATUS } from '../lib/definitions/palette';
import { hazardAdapterStatusInfo, hazardDecisionInfo, HAZARD_DECISION_ORDER, type HazardDecision } from '../lib/definitions/hazards';
import { PAGE_SIZE } from '../lib/definitions/navigation';
import { type Protocol } from '../lib/definitions/protocols';
import { transportIdProtocol } from "../lib/transportProtocol";
import { regionBBox } from "../lib/regions";
import { formatNumber, relativeTime } from "../lib/format";
import { AutomationsProtocolPanel } from "../components/AutomationsTab";
import { AlertsFamiliesPanel } from "../components/AlertsFamilyCards";
import { PagingBar } from "../components/PagingBar";
import type { HazardAdapter, HazardEvent, HazardEventsResponse, RegionDef } from "../api/types";

// Renamed "Hazards" -> "Alerts" per the information-architecture spec, then
// rebuilt into 5 tabs per the full IA rebuild directive: the ALERTS menu at
// the top must be BROADCASTS then ALERTS as a submenu, then Meshtastic and
// MeshCore below that, with no drop downs — simply tabs. Route id, file
// name, backend feature id (hazard_adapters),
// and every /api/hazards/* path stay as-is — the rename is display-only
// (see lib/definitions.ts's NAV_DESTINATIONS comment).
//
// Tab order and purpose:
// - Broadcasts: the real automation engine (auto-reply, scheduled
//   announcement, welcome-node, cold-start-grace) against the backend
//   contract the coordinator specified — see AutomationsTab.tsx's own
//   header comment for the full contract and its safety reasoning. This
//   REPLACES the generic rule-builder that was rejected outright as a
//   hard pass on that entire section.
// - Alerts: per-family guided cards (enabled/severity/poll-interval/
//   channel) driven by the real, already-PATCHable hazards.sources and
//   hazards.region_routes config — see AlertsFamilyCards.tsx's own header
//   comment. Meshtastic/MeshCore as sub-tabs, not a dropdown.
// - Logs: the former single events table (originally "Broadcast" tab),
//   moved here wholesale, split by the protocol each event would route to
//   (would_send_to[].transport). Its adapter/decision filters are tab
//   rows, not <select>s — "no drop downs, simply tabs" is a
//   general instruction, not scoped to the top nav.
// - Regions: read-only view (HOLD — the region model is being reverted
//   from anchor points back to bounding boxes; this stays exactly as it
//   was until that contract lands, per explicit instruction not to build
//   region-shaped UI in the meantime).
// - Sources: today's adapter-health table, unchanged.
//
// Phase 6 framing (unchanged from the original Hazards build, applies to
// Logs): hazard adapters are authorised only on the condition that nothing
// transmits, so "what would have gone out, and why it didn't" is the only
// observable output this phase has for the Logs tab specifically.
// - "suppressed" is the system working correctly and must never read as a
//   wall of failures — HAZARD_DECISION_INFO keeps it off
//   STATUS.critical/serious on purpose.
// - `rendered_message` is shown verbatim on the detail page only; this
//   tab's table uses the API's own separate `headline` field for its
//   scannable column, never a truncation of the transmit text.
// - `sent` renders inert when false, loud when true — the one flag that
//   would mean the phase-6 no-transmit guarantee was actually broken.
// The Broadcasts tab is a deliberate, coordinator-authorized exception to
// that no-transmit framing: its Preview action is a real backend call,
// safe only because the dispatcher's transport allowlist contains
// nothing but the simulated transport while live transmit is off.
type Loadable<T> = T | null | "unavailable";

type AlertsTab = "broadcasts" | "alerts" | "logs" | "regions" | "sources";
const ALERTS_TAB_ORDER: AlertsTab[] = ["broadcasts", "alerts", "logs", "regions", "sources"];
const ALERTS_TAB_LABEL: Record<AlertsTab, string> = {
  broadcasts: "Broadcasts",
  alerts: "Alerts",
  logs: "Logs",
  regions: "Regions",
  sources: "Sources",
};

function isAlertsTab(x: string | null | undefined): x is AlertsTab {
  return x === "broadcasts" || x === "alerts" || x === "logs" || x === "regions" || x === "sources";
}

function humanizeCode(code: string): string {
  const s = code.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// A single bounded full-scan of the current filter (window/adapter/
// decision), not server-side cursor paging — because splitting by
// protocol needs to look at every event's `would_send_to`, and
// /api/hazards/events has no transport/protocol query param (confirmed
// via OpenAPI, same gap as the earlier reason-code breakdown). Paging the
// raw cursor stream and filtering each page client-side would produce the
// same hollow-page problem that breakdown was built to avoid: whichever
// protocol is under-represented on a given raw page would show a
// near-empty page with a misleading "hasNext." One scan here serves both
// protocol sections' tables, counts, and reason breakdowns — capped, with
// an honest note if a window is large enough to hit the cap (matches the
// convention already used for the Reachability disagreements scan).
const SCAN_PAGE_LIMIT = 500;
const SCAN_MAX_PAGES = 8; // 8 * 500 = 4,000 events of headroom

interface EventScanState {
  loading: boolean;
  events: HazardEvent[];
  scanned: number;
  truncated: boolean;
}

function useScannedHazardEvents(baseParams: Record<string, string>): EventScanState {
  const paramsKey = JSON.stringify(baseParams);
  const [state, setState] = useState<EventScanState>({ loading: true, events: [], scanned: 0, truncated: false });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, events: [], scanned: 0, truncated: false });
    (async () => {
      const events: HazardEvent[] = [];
      let cursor: string | null = null;
      let scanned = 0;
      let truncated = false;
      for (let page = 0; page < SCAN_MAX_PAGES; page++) {
        const search = new URLSearchParams(baseParams);
        search.set("limit", String(SCAN_PAGE_LIMIT));
        if (cursor) search.set("cursor", cursor);
        const res = await tryApiGet<HazardEventsResponse>(`/api/hazards/events?${search.toString()}`);
        if (cancelled) return;
        if (!res) {
          truncated = true;
          break;
        }
        events.push(...res.events);
        scanned += res.events.length;
        if (!res.next_cursor) break;
        cursor = res.next_cursor;
        if (page === SCAN_MAX_PAGES - 1) truncated = true;
      }
      if (!cancelled) setState({ loading: false, events, scanned, truncated });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  return state;
}

function BroadcastProtocolSection({
  heading,
  events,
}: {
  heading: string;
  events: HazardEvent[];
}) {
  const { effectiveTheme } = useAppData();
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [events]);

  const counts = useMemo(() => {
    const c: Record<HazardDecision, number> = { would_broadcast: 0, suppressed: 0 };
    for (const e of events) c[e.decision] = (c[e.decision] ?? 0) + 1;
    return c;
  }, [events]);

  const breakdownRows = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of events) {
      const key = `${e.decision}::${e.reason_code}`;
      m.set(key, (m.get(key) ?? 0) + 1);
    }
    return Array.from(m.entries())
      .map(([key, count]) => {
        const [decision, reasonCode] = key.split("::") as [HazardDecision, string];
        return { decision, reasonCode, count };
      })
      .sort((a, b) => b.count - a.count);
  }, [events]);

  const pageItems = events.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const rangeStart = pageItems.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = rangeStart === 0 ? 0 : rangeStart + pageItems.length - 1;

  function goToEvent(e: HazardEvent) {
    navigate(`#/hazards/${encodeURIComponent(e.id)}`);
  }

  return (
    <section className="page-section">
      <div className="page-section-title">{heading}</div>

      {events.length === 0 ? (
        <div className="empty-state">No {heading} events in this window.</div>
      ) : (
        <>
          <div className="audit-counts">
            {HAZARD_DECISION_ORDER.map((d) => {
              const info = hazardDecisionInfo(d);
              return (
                <span key={d} className="badge" style={{ background: colorFor(info.color, effectiveTheme) }}>
                  {info.label}: {formatNumber(counts[d] ?? 0)}
                </span>
              );
            })}
          </div>

          <div className="page-section-title" style={{ marginTop: 10, fontSize: 11 }}>
            By reason
          </div>
          <div className="audit-counts">
            {breakdownRows.map((r) => {
              const decisionInfo = hazardDecisionInfo(r.decision);
              return (
                <span
                  key={`${r.decision}::${r.reasonCode}`}
                  className="badge"
                  style={{ background: colorFor(decisionInfo.color, effectiveTheme) }}
                  title={`${decisionInfo.label} · ${humanizeCode(r.reasonCode)}`}
                >
                  {humanizeCode(r.reasonCode)}: {formatNumber(r.count)}
                </span>
              );
            })}
          </div>

          <div className="node-list-table-wrap" style={{ marginTop: 10 }}>
            <table className="node-list-table">
              <thead>
                <tr>
                  <th>Decision</th>
                  <th>Adapter</th>
                  <th>Headline</th>
                  <th>Reason</th>
                  <th>Severity</th>
                  <th>Regions</th>
                  <th>Fetched</th>
                  <th>Sent</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((e) => {
                  const info = hazardDecisionInfo(e.decision);
                  return (
                    <tr key={e.id} className="node-list-row" onClick={() => goToEvent(e)}>
                      <td>
                        <span className="badge" style={{ background: colorFor(info.color, effectiveTheme) }}>
                          {info.label}
                        </span>
                      </td>
                      <td>{e.adapter_label}</td>
                      <td style={{ maxWidth: 360 }} title={e.headline}>
                        {e.headline}
                      </td>
                      <td>{humanizeCode(e.reason_code)}</td>
                      <td>{e.severity || "--"}</td>
                      <td>{e.regions.length > 0 ? e.regions.join(", ") : "--"}</td>
                      <td>{relativeTime(e.fetched_at)}</td>
                      <td>
                        {e.sent ? (
                          <span className="badge" style={{ background: colorFor(STATUS.critical, effectiveTheme) }}>
                            SENT
                          </span>
                        ) : (
                          "--"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <PagingBar
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={events.length}
            hasPrev={page > 0}
            hasNext={(page + 1) * PAGE_SIZE < events.length}
            onPrev={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        </>
      )}
    </section>
  );
}

function LogsTab({ adapters }: { adapters: HazardAdapter[] }) {
  const [windowKey, setWindowKey] = useState<LiveWindow>("24h");
  const [adapterFilter, setAdapterFilter] = useState("all");
  const [decisionFilter, setDecisionFilter] = useState("all");

  const queryParams = useMemo(() => {
    const p: Record<string, string> = { window: windowKey };
    if (adapterFilter !== "all") p.adapter = adapterFilter;
    if (decisionFilter !== "all") p.decision = decisionFilter;
    return p;
  }, [windowKey, adapterFilter, decisionFilter]);

  const scan = useScannedHazardEvents(queryParams);

  const meshtasticEvents = useMemo(
    () => scan.events.filter((e) => e.would_send_to.some((w) => transportIdProtocol(w.transport) === "meshtastic")),
    [scan.events],
  );
  const meshcoreEvents = useMemo(
    () => scan.events.filter((e) => e.would_send_to.some((w) => transportIdProtocol(w.transport) === "meshcore")),
    [scan.events],
  );
  // Not silently dropped: an event whose would_send_to resolves to neither
  // protocol (e.g. a transport id this heuristic doesn't recognise, or a
  // route that only ever reaches the simulated transport) is called out
  // by count rather than vanishing from the page — every event stays
  // reachable somewhere, same rule this engagement has applied to
  // detectors and config fields.
  const unmatchedCount = scan.events.filter((e) => !e.would_send_to.some((w) => transportIdProtocol(w.transport))).length;

  return (
    <>
      <div className="node-list-toolbar">
        <div className="theme-toggle" role="group" aria-label="Window">
          {LIVE_WINDOW_ORDER.map((w) => (
            <button key={w} data-active={windowKey === w} onClick={() => setWindowKey(w)}>
              {w}
            </button>
          ))}
        </div>
        <div className="theme-toggle" role="group" aria-label="Adapter">
          <button data-active={adapterFilter === "all"} onClick={() => setAdapterFilter("all")}>
            All adapters
          </button>
          {adapters.map((a) => (
            <button key={a.id} data-active={adapterFilter === a.id} onClick={() => setAdapterFilter(a.id)}>
              {a.label}
            </button>
          ))}
        </div>
        <div className="theme-toggle" role="group" aria-label="Decision">
          <button data-active={decisionFilter === "all"} onClick={() => setDecisionFilter("all")}>
            All decisions
          </button>
          {HAZARD_DECISION_ORDER.map((d) => (
            <button key={d} data-active={decisionFilter === d} onClick={() => setDecisionFilter(d)}>
              {hazardDecisionInfo(d).label}
            </button>
          ))}
        </div>
      </div>

      {scan.loading && <div className="node-detail-note">Scanning the window…</div>}
      {!scan.loading && scan.truncated && (
        <div className="node-detail-note">
          Scanned the first {formatNumber(scan.scanned)} event(s) in this window — there may be more; counts and sections below
          reflect only what was scanned.
        </div>
      )}

      {!scan.loading && (
        <>
          <BroadcastProtocolSection heading="Meshtastic" events={meshtasticEvents} />
          <BroadcastProtocolSection heading="MeshCore" events={meshcoreEvents} />
          {unmatchedCount > 0 && (
            <div className="node-detail-note">
              {formatNumber(unmatchedCount)} event(s) in this window don't resolve to either protocol's transport (e.g.
              simulated-only routing) — not shown in either section above.
            </div>
          )}
        </>
      )}
    </>
  );
}

// Meshtastic/MeshCore sub-tabs shared by Broadcasts and Alerts — a button
// row, never a dropdown, per "no drop downs. Simply tabs."
function ProtocolSubTabs({ protocol, onChange }: { protocol: Protocol; onChange: (p: Protocol) => void }) {
  return (
    <div className="theme-toggle" role="group" aria-label="Protocol" style={{ marginBottom: 10 }}>
      <button data-active={protocol === "meshtastic"} onClick={() => onChange("meshtastic")}>
        Meshtastic
      </button>
      <button data-active={protocol === "meshcore"} onClick={() => onChange("meshcore")}>
        MeshCore
      </button>
    </div>
  );
}

function BroadcastsTab() {
  const [protocol, setProtocol] = useState<Protocol>("meshtastic");
  return (
    <>
      <ProtocolSubTabs protocol={protocol} onChange={setProtocol} />
      <AutomationsProtocolPanel protocol={protocol} />
    </>
  );
}

function AlertsFamiliesTab({ adapters }: { adapters: HazardAdapter[] }) {
  const [protocol, setProtocol] = useState<Protocol>("meshtastic");
  return (
    <>
      <ProtocolSubTabs protocol={protocol} onChange={setProtocol} />
      <AlertsFamiliesPanel protocol={protocol} adapters={adapters} />
    </>
  );
}

// "--" means the field itself is missing (unknown); "(none)" means the
// field IS present and is a deliberately empty answer — meshmapper.net,
// a common source for a region's starting bbox, has no local_name/
// description/cities/aliases/nws_zones data, and the backend correctly
// declined to invent any rather than leave the field out. Rendering both the same way
// would read as "this data failed to load," which isn't true.
function textCell(v: string | undefined): string {
  if (v === undefined) return "--";
  return v === "" ? "(none)" : v;
}

function listCell(v: string[] | undefined): string {
  if (v === undefined) return "--";
  return v.length > 0 ? v.join(", ") : "(none)";
}

function regionBoundsLabel(r: RegionDef): string {
  const box = regionBBox(r);
  if (!box) return "--";
  return `${box.minLat}..${box.maxLat}, ${box.minLon}..${box.maxLon}`;
}

function RegionsTab() {
  const { regions, regionsAvailable } = useAppData();

  return (
    <section className="page-section">
      <div className="page-section-title">Regions</div>
      <div className="node-detail-note">
        Read-only here — bounding-box areas (a point can fall inside more than one, since boxes can overlap). Add, edit, or remove regions under Settings &gt; Regions.
      </div>
      {!regionsAvailable && <div className="node-detail-note">Regions data hasn't answered yet.</div>}
      {regionsAvailable &&
        (regions.length === 0 ? (
          <div className="empty-state">No regions configured yet.</div>
        ) : (
          <div className="node-list-table-wrap">
            <table className="node-list-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Local name</th>
                  <th>Bounds (lat, lon)</th>
                  <th>Cities</th>
                  <th>Aliases</th>
                  <th>NWS zones</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {regions.map((r, i) => (
                  <tr key={r.name ?? i}>
                    <td>{textCell(r.name)}</td>
                    <td>{textCell(r.local_name)}</td>
                    <td>{regionBoundsLabel(r)}</td>
                    <td>{listCell(r.cities)}</td>
                    <td>{listCell(r.aliases)}</td>
                    <td>{listCell(r.nws_zones)}</td>
                    <td>{textCell(r.description)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </section>
  );
}

function SourcesTab({ adapters }: { adapters: Loadable<HazardAdapter[]> }) {
  const { effectiveTheme } = useAppData();
  return (
    <section className="page-section">
      <div className="page-section-title">Adapter health</div>
      {adapters === null && <div className="node-detail-note">Loading…</div>}
      {adapters === "unavailable" && <div className="node-detail-note">Hazard adapter status didn't respond as expected.</div>}
      {Array.isArray(adapters) &&
        (adapters.length === 0 ? (
          <div className="empty-state">No hazard adapters configured on this instance.</div>
        ) : (
          <div className="node-list-table-wrap">
            <table className="node-list-table">
              <thead>
                <tr>
                  <th>Adapter</th>
                  <th>Status</th>
                  <th>Enabled</th>
                  <th>Last run</th>
                  <th>Next run</th>
                  <th>Upstream</th>
                  <th>Last error</th>
                </tr>
              </thead>
              <tbody>
                {adapters.map((a) => {
                  const info = hazardAdapterStatusInfo(a.last_status);
                  return (
                    <tr key={a.id}>
                      <td>{a.label}</td>
                      <td>
                        <span className="badge" style={{ background: colorFor(info.color, effectiveTheme) }}>
                          {info.label}
                        </span>
                      </td>
                      <td>{a.enabled ? "Yes" : "No"}</td>
                      <td>{relativeTime(a.last_run_at)}</td>
                      <td>{a.next_run_at ? relativeTime(a.next_run_at) : "--"}</td>
                      <td>{a.upstream_stale ? "Stale" : "Fresh"}</td>
                      {/* Full text, wrapping normally — not truncated into
                          uselessness (explicit requirement). This table's
                          <td> has no forced nowrap/ellipsis, so a long
                          error simply wraps onto more lines. */}
                      <td style={{ maxWidth: 420 }}>{a.last_error || "--"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
    </section>
  );
}

export function HazardsPage({ presetTab }: { presetTab?: string | null }) {
  const [tab, setTab] = useState<AlertsTab>(() => (isAlertsTab(presetTab) ? presetTab : "broadcasts"));
  const [adapters, setAdapters] = useState<Loadable<HazardAdapter[]>>(null);

  useEffect(() => {
    if (isAlertsTab(presetTab)) setTab(presetTab);
  }, [presetTab]);

  useEffect(() => {
    let cancelled = false;
    function poll() {
      tryApiGet<{ adapters: HazardAdapter[] }>("/api/hazards/adapters")
        .then((r) => !cancelled && setAdapters(r?.adapters ?? "unavailable"))
        .catch(() => !cancelled && setAdapters("unavailable"));
    }
    poll();
    const id = window.setInterval(poll, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  return (
    <div className="node-list-page">
      <h2 className="audit-page-title">Alerts</h2>

      <div className="theme-toggle" role="group" aria-label="Alerts tab" style={{ marginBottom: 10 }}>
        {ALERTS_TAB_ORDER.map((t) => (
          <button key={t} data-active={tab === t} onClick={() => setTab(t)}>
            {ALERTS_TAB_LABEL[t]}
          </button>
        ))}
      </div>

      {tab === "broadcasts" && <BroadcastsTab />}
      {tab === "alerts" && <AlertsFamiliesTab adapters={Array.isArray(adapters) ? adapters : []} />}
      {tab === "logs" && <LogsTab adapters={Array.isArray(adapters) ? adapters : []} />}
      {tab === "regions" && <RegionsTab />}
      {tab === "sources" && <SourcesTab adapters={adapters} />}
    </div>
  );
}
